import { Fragment, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { Container, Divider, Grid } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { LocaleContext } from "../../routing/LangRouter";

//import utils
import { fixNumbers, scrollToPosition } from "../../utils/utility";
import { formatPersianDate } from "../../utils/date";
import { passengersToPersonCounter } from "../../utils/passengersToPersonCounter";

//import constants
import { IS_MOBILE } from "../../constants/breakpoints";

//import general component
import MobileHeader from "../../components/common/mobile-header/mobile-header";
import BaseTitle from "../../components/common/base-title/base-title";
import { BaseButton } from "../../components/common/base-button/base-button";
import OldPassengersModal from "../../components/view/passengers-forms/old-passengers-modal";
import TabPanel from "../../components/common/base-tab/base-tabs-panel";
import BaseTabPanel from "../../components/common/base-tab/base-tab-panel";
import SelectedItem from "../../components/common/selected-item/selected-item";

//import view components
import PassengersFormContainer from "../../components/view/passengers-forms/passengers-form-container";
import RulesLink from "../rules/rules-link";

//import images and icons
import Icon from "../../components/common/base-icon/icon";
import { InfoCircle } from "iconsax-react";

//import styles
import "./passengers.scss";

export default function IntFlightPassengers() {
    //general variables
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const accessToken = localStorage.getItem("accessToken");
    // const [isLoginOpen, setIsLoginOpen] = useState(false);

    //sessionStorage variables
    const { isAccommodation, isItinerary, isHotelInternational, isAutomaticReserve } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const intFlightInformation = JSON.parse(sessionStorage.getItem("intFlightInformation"));
    const selectedIntFlight = JSON.parse(sessionStorage.getItem("selectedIntFlight"));
    const intFlightPassengers = JSON.parse(sessionStorage.getItem("intFlightPassengers"));
    const { tag } = JSON.parse(sessionStorage.getItem("selectedHotel")) || {};
    const selectedReservation = JSON.parse(sessionStorage.getItem("selectedReservation")) || {};
    const { item } = selectedReservation;

    useEffect(() => {
        scrollToPosition();
    }, []);

    //generate an array of passenger types for the automatic reservation
    const { personCounter } = passengersToPersonCounter?.(item?.passengers, selectedReservation);

    //generate an array of passenger types
    const passengerTypes = () => {
        const { adult, child, infant } = intFlightInformation?.personCounter || personCounter;
        const passengerCount = { adult, child, infant };
        const passengerTypesArray = [];

        Object.entries(passengerCount).forEach(([type, count]) => {
            for (let i = 0; i < parseInt(count); i++) {
                passengerTypesArray.push(type);
            }
        });

        return passengerTypesArray;
    };

    //passenger type code
    const PASSENGER_TYPE_CODES = {
        adult: "adt",
        child: "chd",
        infant: "inf",
    };

    //initial value for forms
    const defaultValues = intFlightPassengers ?? {
        passengers: passengerTypes()?.map(() => {
            return {
                english_first_name: "",
                middle_name: "",
                english_last_name: "",
                title: "",
                passport_no: "",
                passport_country: "",
                passport_exp_date: "",
                passport_issue_date: "",
                national_code: "",
                nationality: "",
                gender: "",
                type: "",
                birthdate: "",
            };
        }),
    };

    const {
        control,
        register,
        handleSubmit,
        watch,
        setValue,
        getValues,
        resetField,
        clearErrors,
        formState: { errors },
    } = useForm({
        defaultValues: defaultValues,
        mode: "all",
        delayError: 500,
    });

    const { fields: forms } = useFieldArray({
        control,
        name: "passengers",
    });

    const handleRegistration = data => {
        if (IS_MOBILE) {
            // Create array of required fields
            const requiredFields = [
                "english_first_name",
                "english_last_name",
                "nationality",
                "gender",
                "birthdate",
                "passport_no",
                "passport_country",
                "passport_exp_date",
            ];

            // Validate each passenger form
            const isValid = data.passengers.every((formData, index) => {
                const idField = IDcard[index]?.nationalCode ? "national_code" : "passport_no";
                const allFields = [...requiredFields, idField];

                const isFormValid = allFields.every(field => formData[field]);

                if (!isFormValid) {
                    setTabValue(index);
                    toast.error(t("Toast Messages.Complete Forms for Each Passenger"));
                    return false;
                }
                return true;
            });

            if (!isValid) return;
        }
        const convertedData = {
            ...data,
            passengers: data.passengers.map(passenger => ({
                ...passenger,
                national_code: fixNumbers(passenger.national_code),
                passport_no: fixNumbers(passenger.passport_no),
            })),
        };

        if (!!accessToken) {
            sessionStorage.setItem("intFlightPassengers", JSON.stringify(convertedData));
            if (isItinerary) {
                if (isAccommodation && !isHotelInternational && tag !== "pr") {
                    navigate(`/${locale}/hotel-passengers`);
                } else if (isAccommodation && (isHotelInternational || tag === "pr")) {
                    navigate(`/${locale}/int-hotel-passengers`);
                } else {
                    navigate(`/${locale}/confirm-information`);
                }
            } else if (isAutomaticReserve) {
                navigate(`/${locale}/flight-automatic-reserve/confirm`);
            } else {
                navigate(`/${locale}/confirm-information`);
            }
        } else {
            setSearchParams({ action: "login" });
        }
    };

    const handleError = error => {
        console.log(error);
    };

    //value for id and rate plan id rooms
    useEffect(() => {
        setTimeout(() => {
            passengerTypes().forEach((type, index) => {
                setValue(`passengers.${index}.type`, PASSENGER_TYPE_CODES[type]);
            });
        }, 1000);
    }, []);

    //handel the selection of passenger from previous passenger
    const [IDcard, setIDcard] = useState([]);
    const [isPassengersModalOpen, setIsPassengersModalOpen] = useState(false);
    const handleSelectPassenger = (passenger, index) => {
        clearErrors("passengers");
        setValue(
            `passengers.${index}`,
            {
                type: PASSENGER_TYPE_CODES[passengerTypes()[index]],
                english_first_name: passenger.english_first_name,
                english_last_name: passenger.english_last_name,
                gender: passenger.gender,
                birthdate: passenger.birthdate,
                passport_no: passenger.passport_no,
                passport_country: passenger?.passport_issue_country,
                passport_exp_date: passenger.passport_expiry_date,
                passport_issue_date: passenger.passport_issue_date,
                nationality: passenger.nationality,
                national_code: passenger.national_code,
            },
            { shouldValidate: true },
        );
    };

    //handle modals
    useEffect(() => {
        if (searchParams.get("action") === "old-passengers") setIsPassengersModalOpen(true);
        else setIsPassengersModalOpen(false);
    }, [searchParams]);

    //handle form tabs on mobile
    const [tabValue, setTabValue] = useState(0);
    const handleTabChange = (event, newValue) => {
        if (!errors.passengers?.[tabValue]) {
            scrollToPosition(0, 0);
            setTabValue(newValue);
        }
    };

    //handle navigate back on Mobile
    const navigateBack = () => {
        if (isItinerary) navigate(`/${locale}/cart`, { replace: true });
        else navigate(`/${locale}/international-flight`, { replace: true });
    };

    const data = selectedIntFlight && {
        title: `${t("departureTicket")}  ${intFlightInformation?.returnDate ? t("Flight.And Return") : ""}`,
        name: [
            ...(selectedIntFlight?.segments?.map(segment =>
                locale === "en" ? segment?.departure?.city?.english : segment?.departure?.city?.persian,
            ) ?? []),
            selectedIntFlight?.segments?.[selectedIntFlight?.segments?.length - 1]?.destination?.city?.persian,
        ],
        date: (
            <Grid container item xs={12} direction="column">
                <span>
                    {t("departureDate")}:{" "}
                    {formatPersianDate(selectedIntFlight?.segments?.[0]?.departure_date, {
                        day: "numeric",
                        month: "long",
                        year: undefined,
                        weekday: "long",
                    })}{" "}
                    - {selectedIntFlight?.segments?.[0]?.departure_time.split(":", 2).join(":")}
                </span>
                {intFlightInformation?.returnDate && (
                    <span>
                        {t("returnDate")}:{" "}
                        {formatPersianDate(selectedIntFlight?.return_segments?.[0]?.departure_date, {
                            day: "numeric",
                            month: "long",
                            year: undefined,
                            weekday: "long",
                        })}{" "}
                        - {selectedIntFlight?.return_segments?.[0]?.departure_time.split(":", 2).join(":")}
                    </span>
                )}
            </Grid>
        ),
        type: "intFlight",
        details: selectedIntFlight,
        route: "international-flight",
    };

    return (
        <Container maxWidth="xl">
            <Grid className="passengers" container item xs={12} rowGap={3} mb={forms?.length > 1 ? 30 : 26}>
                <MobileHeader title={t("passengersInformation")} justifyContent="center" handleClick={navigateBack} />
                {selectedIntFlight?.segments?.[0]?.departure_date && ( // avoid showing selected item on the automatic reserve flow when a thicket is not selected
                    <Grid container item xs={12} rowGap={3} columnGap={1} mt={IS_MOBILE ? 7 : 0}>
                        <SelectedItem data={data} />
                    </Grid>
                )}

                <Grid component="form" container item xs={12} rowGap={3} onSubmit={handleSubmit(handleRegistration, handleError)}>
                    {IS_MOBILE ? (
                        <Grid className="passengers__forms" container item xs={12}>
                            {forms.map((form, formIndex) => {
                                return (
                                    <TabPanel value={tabValue} index={formIndex}>
                                        <PassengersFormContainer
                                            formIndex={formIndex}
                                            formType="intFlight"
                                            passengerType={t(`${passengerTypes()[formIndex]}`)}
                                            forms={forms}
                                            form={form}
                                            formCount={passengerTypes()}
                                            IDcard={IDcard}
                                            setIDcard={setIDcard}
                                            {...{ control, register, defaultValues, watch, setValue, getValues, resetField, errors }}
                                        />
                                    </TabPanel>
                                );
                            })}

                            {forms?.length > 1 ? (
                                <BaseTabPanel
                                    list={forms}
                                    labels={passengerTypes()}
                                    tabValue={tabValue}
                                    handleTabChange={handleTabChange}
                                    isError={errors.passengers?.[tabValue]}
                                />
                            ) : null}
                        </Grid>
                    ) : (
                        <>
                            <BaseTitle
                                title={
                                    <Grid container item xs={12} columnGap={1} justifyContent="space-between" alignItems="center">
                                        <Icon name="PassengerIcon" /> {t("passengersInformation")}
                                    </Grid>
                                }
                                dividerColor="1px solid var(--medium-gray-color)"
                            />

                            <Grid className="passengers__date-warning" component="span" container item xs={12} columnGap={1}>
                                <InfoCircle size="20" color="var(--mustard-color)" />
                                توجه داشته باشید که سن مسافر با توجه به تاریخ آخرین پرواز در حال رزرو محاسبه می شود.
                            </Grid>

                            <Grid className="passengers__forms" container item xs={12} rowGap={5}>
                                {forms.map((form, formIndex) => {
                                    return (
                                        <Fragment key={form.id}>
                                            <PassengersFormContainer
                                                formIndex={formIndex}
                                                formType="intFlight"
                                                passengerType={t(`${passengerTypes()[formIndex]}`)}
                                                form={form}
                                                formCount={passengerTypes()}
                                                IDcard={IDcard}
                                                setIDcard={setIDcard}
                                                {...{ control, register, defaultValues, watch, setValue, getValues, resetField, errors }}
                                            />
                                            {formIndex < forms.length - 1 && <Divider width="100%" />}
                                        </Fragment>
                                    );
                                })}
                            </Grid>
                        </>
                    )}
                    <Grid
                        className="passengers__submit-information"
                        container
                        item
                        xs={12}
                        rowGap={2}
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <RulesLink />
                        <Grid container item xs={12} md={2} justifyContent="flex-end">
                            <BaseButton text={t("submitInformation")} color="primary" size="large" radius="radius-10" type="submit" />
                        </Grid>
                    </Grid>
                </Grid>

                <OldPassengersModal open={isPassengersModalOpen} setOpen={setIsPassengersModalOpen} selectedOldPassenger={handleSelectPassenger} />
            </Grid>
        </Container>
    );
}
